/**
 * RogueliteHUDRenderer — zone indicator, weapon name + mod badges, grapple status.
 *
 * Positioned top-left below the title. Only visible during roguelite mode gameplay.
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { Z } from '../../constants';
import type { GameState } from '../../types';
import { modeIsRoguelite } from '../../game/mode';

function monoStyle(size: number, color: number, bold = false): TextStyle {
  return new TextStyle({
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: size,
    fill: color,
    fontWeight: bold ? 'bold' : 'normal',
    stroke: { color: 0x000000, width: 2 },
  });
}

export class RogueliteHUDRenderer {
  private container: Container;
  private zoneText: Text;
  private weaponText: Text;
  private modsText: Text;
  private grappleText: Text;

  constructor(uiContainer: Container) {
    this.container = new Container();
    this.container.zIndex = Z.HUD_BASE;
    this.container.visible = false;
    uiContainer.addChild(this.container);

    // Zone indicator
    this.zoneText = new Text({
      text: 'ZONE 1',
      style: monoStyle(16, 0xffaa00, true),
    });
    this.zoneText.position.set(20, 60);
    this.container.addChild(this.zoneText);

    // Weapon info
    this.weaponText = new Text({
      text: '',
      style: monoStyle(13, 0xffffff),
    });
    this.weaponText.position.set(20, 82);
    this.container.addChild(this.weaponText);

    // Modifier badges
    this.modsText = new Text({
      text: '',
      style: monoStyle(11, 0x888888),
    });
    this.modsText.position.set(20, 100);
    this.container.addChild(this.modsText);

    // Grapple status
    this.grappleText = new Text({
      text: '',
      style: monoStyle(11, 0x44ff88),
    });
    this.grappleText.position.set(20, 118);
    this.container.addChild(this.grappleText);
  }

  update(state: GameState): void {
    if (!modeIsRoguelite(state.modeState.mode)) {
      this.container.visible = false;
      return;
    }

    const isMenu = state.phase === 'title_screen' || state.phase === 'mode_select' ||
                   state.phase === 'tank_select' || state.phase === 'stage_select';
    if (isMenu) {
      this.container.visible = false;
      return;
    }

    this.container.visible = true;
    const ms = state.modeState;

    // Zone
    this.zoneText.text = `ZONE ${ms.rogueliteZone}`;

    // Weapon info
    const shot1Name = ms.rogueliteShot1?.name ?? 'MORTAR';
    const shot2Name = ms.rogueliteShot2?.name ?? null;
    if (shot2Name) {
      this.weaponText.text = `[1] ${shot1Name}  [2] ${shot2Name}`;
    } else {
      this.weaponText.text = shot1Name;
    }

    // Mods
    const allMods = [...ms.rogueliteShot1Mods, ...ms.rogueliteShot2Mods];
    this.modsText.text = allMods.length > 0 ? allMods.join(' + ') : '';

    // Grapple
    if (ms.rogueliteHasGrapple) {
      const ammo = state.grapple.ammo[0] ?? 0;
      this.grappleText.text = `GRAPPLE: ${ammo}`;
      this.grappleText.visible = true;
    } else {
      this.grappleText.visible = false;
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
